import { useState } from 'react'
import { Button } from '../../components/ui/Button'
import { Dialog } from '../../components/ui/Dialog'
import { useToast } from '../../components/feedback/toastContext'
import { faNumber } from '../../lib/faNumbers'
import {
  isRetryable,
  isTransferable,
  rejectReasonShort,
  rejectReasonText,
} from '../../lib/rejectReasonText'
import { clockFa } from '../../lib/relativeTime'
import { useOnlineStatus } from '../../lib/useOnlineStatus'
import {
  deleteRejectedOp,
  ignoreRejectedSerial,
  queueTransfer,
  retryRejectedOp,
} from '../../outbox/operations'
import { useFailedSerials, useQueuedCount } from '../../outbox/useOutbox'
import type { FailedSerial } from '../../outbox/types'
import { useHomeStats } from '../Home/useHomeStats'
import styles from './QueueScreen.module.css'

export function QueueScreen() {
  const { show } = useToast()
  const isOnline = useOnlineStatus()
  const stats = useHomeStats()
  const queuedCount = useQueuedCount()
  const failedSerials = useFailedSerials()
  const [selectedFailed, setSelectedFailed] = useState<FailedSerial | null>(null)

  const retryableFailed = failedSerials.filter((failed) => isRetryable(failed.reason))
  const hasPendingWork = queuedCount > 0

  const closeTriage = () => setSelectedFailed(null)

  const runTriageAction = async (action: () => Promise<void>, message: string) => {
    try {
      await action()
      closeTriage()
      show(message, 'ok')
    } catch {
      show('عملیات انجام نشد', 'err')
    }
  }

  const handleTransfer = (failed: FailedSerial) => {
    void runTriageAction(
      () =>
        queueTransfer(failed.opId, {
          palletCode: failed.palletCode,
          serialNumber: failed.serialNumber,
          captureMethod: failed.captureMethod,
        }).then(() => undefined),
      'درخواست انتقال سریع در صف قرار گرفت',
    )
  }

  const handleRetry = (failed: FailedSerial) => {
    void runTriageAction(
      () => retryRejectedOp(failed.opId),
      'درخواست برای تلاش مجدد در صف قرار گرفت',
    )
  }

  const handleDelete = (failed: FailedSerial) => {
    void runTriageAction(() => deleteRejectedOp(failed.opId), 'مورد ناموفق حذف شد')
  }

  const handleIgnore = (failed: FailedSerial) => {
    void runTriageAction(
      () => ignoreRejectedSerial(failed.opId, failed.serialNumber),
      'مورد ناموفق نادیده گرفته شد',
    )
  }

  const retryAll = async () => {
    if (retryableFailed.length === 0) {
      return
    }

    try {
      await Promise.all(retryableFailed.map((failed) => retryRejectedOp(failed.opId)))
      show('تلاش مجدد ارسال آغاز شد', 'ok')
    } catch {
      show('عملیات انجام نشد', 'err')
    }
  }

  const selectedTransferable = selectedFailed !== null && isTransferable(selectedFailed.reason)
  const selectedRetryable = selectedFailed !== null && isRetryable(selectedFailed.reason)

  return (
    <div className={styles.screen}>
      <div className={styles.statsCard}>
        <div className={styles.statRow}>
          <span className={styles.statLabel}>در انتظار ارسال</span>
          <span className={styles.statValue} dir="ltr">
            {faNumber(queuedCount)}
          </span>
        </div>
        <div className={styles.statRow}>
          <span className={styles.statLabel}>آخرین ارسال موفق</span>
          <span className={styles.statValue} dir="ltr">
            {stats.lastSyncAt === null ? '—' : clockFa(stats.lastSyncAt)}
          </span>
        </div>
        <div className={styles.statRow}>
          <span className={styles.statLabel}>اسکن‌های امروز</span>
          <span className={styles.statValue} dir="ltr">
            {faNumber(stats.todayCount)}
          </span>
        </div>
      </div>

      {!isOnline && (
        <div className={styles.offlineBanner}>
          <div className={styles.offlineTitle}>{faNumber(queuedCount)} اسکن روی دستگاه ذخیره شده</div>
          <div className={styles.offlineCopy}>به‌محض وصل‌شدن شبکه، خودکار ارسال می‌شود</div>
        </div>
      )}

      {failedSerials.length > 0 && (
        <div className={styles.failedCard}>
          <div className={styles.failedTitle}>{faNumber(failedSerials.length)} مورد ناموفق</div>
          <div className={styles.failedCopy}>
            سرور این‌ها را نپذیرفت و نیاز به تصمیم اپراتور دارند
          </div>
        </div>
      )}

      <div className={styles.failedSection}>
        <div className={styles.failedList}>
          {failedSerials.length === 0 ? (
            <div className={styles.empty}>
              {hasPendingWork
                ? 'مورد ناموفقی برای بررسی وجود ندارد'
                : 'همه درخواست‌ها با موفقیت ارسال شده‌اند'}
            </div>
          ) : (
            failedSerials.map((failed) => (
              <button
                key={`${failed.opId}:${failed.serialNumber}`}
                type="button"
                className={styles.failedRow}
                onClick={() => setSelectedFailed(failed)}
              >
                <span className={styles.serial} dir="ltr">
                  {failed.serialNumber}
                </span>
                <span className={styles.reasonChip}>{rejectReasonShort(failed.reason)}</span>
              </button>
            ))
          )}
        </div>
        {failedSerials.length > 0 && (
          <div className={styles.hint}>
            هر مورد را باز کنید تا انتقال، حذف یا نادیده‌گرفتن را انتخاب کنید.
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <Button
          variant="secondary"
          compact
          disabled={retryableFailed.length === 0}
          onClick={() => void retryAll()}
        >
          تلاش مجدد
        </Button>
        <Button
          compact
          disabled={failedSerials.length === 0}
          onClick={() => setSelectedFailed(failedSerials[0] ?? null)}
        >
          بررسی موارد
        </Button>
      </div>

      {selectedFailed !== null && (
        <Dialog
          title="بررسی مورد ناموفق"
          tone="danger"
          subject={selectedFailed.serialNumber}
          customActions={
            <div className={styles.triageActions}>
              {selectedTransferable && (
                <button
                  type="button"
                  className={`${styles.triageAction} ${styles.triageActionPrimary}`}
                  onClick={() => handleTransfer(selectedFailed)}
                >
                  انتقال سریع
                </button>
              )}
              {selectedRetryable && (
                <button
                  type="button"
                  className={`${styles.triageAction} ${styles.triageActionPrimary}`}
                  onClick={() => handleRetry(selectedFailed)}
                >
                  تلاش مجدد
                </button>
              )}
              <button
                type="button"
                className={`${styles.triageAction} ${styles.triageActionDanger}`}
                onClick={() => handleDelete(selectedFailed)}
              >
                حذف
              </button>
              <button
                type="button"
                className={`${styles.triageAction} ${styles.triageActionSecondary}`}
                onClick={() => handleIgnore(selectedFailed)}
              >
                نادیده‌گرفتن
              </button>
              <button
                type="button"
                className={`${styles.triageAction} ${styles.triageActionSecondary}`}
                onClick={closeTriage}
              >
                بستن
              </button>
            </div>
          }
        >
          <div className={styles.triageDetails}>
            <div className={styles.triageLine}>
              <span className={styles.triageLabel}>پالت</span>
              <span className={styles.triageCode} dir="ltr">
                {selectedFailed.palletCode}
              </span>
            </div>
            {selectedFailed.currentPalletCode !== null && (
              <div className={styles.triageLine}>
                <span className={styles.triageLabel}>پالت فعلی سریال</span>
                <span className={styles.triageCode} dir="ltr">
                  {selectedFailed.currentPalletCode}
                </span>
              </div>
            )}
            <p className={styles.triageReason}>{rejectReasonText(selectedFailed.reason)}</p>
          </div>
        </Dialog>
      )}
    </div>
  )
}
